import { Injectable } from '@angular/core';
import { HttpRequest, HttpHandler, HttpEvent, HttpInterceptor, HttpErrorResponse } from '@angular/common/http';
import { throwError, Observable } from 'rxjs';
import { Router } from '@angular/router';
import { catchError, first, switchMap } from 'rxjs/operators';
import { OidcSecurityService } from 'angular-auth-oidc-client';
import Swal from 'sweetalert2';
import { SessionExpiredService } from './session-expired.service';
import { environment } from '../../environments/environment';

const PUBLIC_ROUTES = ['thinking-coach', 'challenge-navigator-print-coach', 'public-store', 'share-profile', 'group-profile'];

// Endpoints served by a static API key (no Bearer token)
const STATIC_KEY_ROUTES = [
    'api/v1/hubspot', 'api/v1/interpretive_guides', 'api/v1/profiles/',
    'api/v1/public', 'api/v1/lambda_functions/invoke.json',
    'api/v1/profile_types.json', 'api/v1/translations'
];

// Query params that classify a profiles/ request as a static-key call
const STATIC_KEY_PROFILE_PARAMS = [
    'profile=custom', 'activity_name=app_sign_up', 'activity_name=profile_download',
    'activity_name=profile_slide_map_start', 'activity_name=profile_slide_map_finish',
    'activity_name=interpretive_guide_download', 'activity_name=quick_guide_download',
    'activity_name=thinking_coach_visited', 'activity_name=collaboration_profile_viewed',
    'activity_name=social_share', 'title=Profile'
];

// Endpoints that require a Bearer token
const BEARER_ROUTES = [
    'api/v1/users', 'api/v1/roster_organizations', 'api/v1/organizations',
    'api/v1/teams', 'api/v1/members', 'api/v1/team_advices', 'api/v1/identities',
    'api/v1/categories', 'api/v1/carts', 'api/v1/addresses', 'api/v1/credit_cards',
    'api/v1/profiles', '/api/v1/identity_claims',
    'https://ny7by57kpfgitmt6yrecrfi4zi.appsync-api.ca-central-1.amazonaws.com/graphql',
    '/api/v1/orders', '/api/v1/products', '/attach_to_customer',
    'api/v1/learner_maps', 'api/v1/learner_map_types', 'api/v1/open_ai',
    '/api/v1/users/cobranding_identity'
];

@Injectable()
export class TokenInterceptor implements HttpInterceptor {

    constructor(
        private router: Router,
        private oidcSecurityService: OidcSecurityService,
        private sessionExpiredService: SessionExpiredService,
    ) {}

    intercept(req: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        return this.oidcSecurityService.getAccessToken().pipe(
            first(),
            switchMap(token => next.handle(this.addAuthHeaders(req, token))),
            catchError((error: HttpErrorResponse) => {
                if (error.status === 401) {
                    const isPublicRoute = PUBLIC_ROUTES.some(r => this.router.url.includes(r));
                    if (!isPublicRoute && !this.sessionExpiredService.popupShown) {
                        this.sessionExpiredService.popupShown = true;
                        this.oidcSecurityService.logoffLocal();
                        const cnFrameworks = localStorage.getItem('CN_FRAMEWORKS');
                        Swal.fire({
                            title: 'Session Expired',
                            text: 'Your session has expired. Please log in again.',
                            icon: 'warning',
                            confirmButtonText: 'Log In',
                            confirmButtonColor: '#18a689',
                            allowOutsideClick: false,
                            allowEscapeKey: false,
                        }).then(() => {
                            localStorage.clear();
                            sessionStorage.clear();
                            if (cnFrameworks) localStorage.setItem('CN_FRAMEWORKS', cnFrameworks);
                            const redirectUri = encodeURIComponent(window.location.origin);
                            window.location.href = `${environment.cognito.domain}/logout?client_id=${environment.cognito.clientId}&redirect_uri=${redirectUri}&response_type=${environment.cognito.responseType}`;
                        });
                    }
                }
                return throwError(error);
            })
        );
    }

    private addAuthHeaders(req: HttpRequest<any>, token: string): HttpRequest<any> {
        // On public routes, learner_maps uses the static key instead of Bearer
        const isOnPublicRoute = this.router.url.includes('thinking-coach') || this.router.url.includes('challenge-navigator-print-coach');
        const staticKeyRoutes = isOnPublicRoute ? [...STATIC_KEY_ROUTES, 'api/v1/learner_maps'] : STATIC_KEY_ROUTES;

        // Static API key endpoints
        const needsStaticKey = staticKeyRoutes.some(route => {
            if (!req.url.includes(route)) return false;
            if (route === 'api/v1/profiles/') {
                return STATIC_KEY_PROFILE_PARAMS.some(p => req.urlWithParams.includes(p));
            }
            return true;
        });

        if (needsStaticKey) {
            return req.clone({ setHeaders: { Authorization: environment.cognito.staticApiKey } });
        }

        // Bearer token for authenticated endpoints
        if (token) {
            const impersonate = localStorage.getItem('impersonation') === 'true';
            const needsBearer = BEARER_ROUTES.some(route => req.url.includes(route));
            if (needsBearer || impersonate) {
                const headers: { [key: string]: string } = { Authorization: 'Bearer ' + token };
                if (impersonate) headers['Impersonate'] = 'true';
                return req.clone({ setHeaders: headers });
            }
        }

        // Impersonation without access token — use id_token from localStorage
        if (!token && localStorage.getItem('impersonation') === 'true') {
            const idToken = localStorage.getItem('id_token');
            return req.clone({ setHeaders: { Authorization: 'Bearer ' + idToken, Impersonate: 'true' } });
        }

        return req;
    }
}
